import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Button,
  IconButton,
  Alert,
  Chip,
  Fade,
  alpha,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  FavoriteBorder,
  Favorite,
  Delete,
  ShoppingCart,
  LocalOffer,
  TrendingUp,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { cartApi } from '../api/cart';
import accountService from '../services/accountService';
import recommendationsService from '../services/recommendationsService';
import secureLocalStorage from 'react-secure-storage';
import Seo from '../components/Seo';

const Wishlist = () => {
  const navigate = useNavigate();
  const [wishlistItems, setWishlistItems] = useState([]);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [error, setError] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [toggledItemId, setToggledItemId] = useState(null);

  // Check authentication and load wishlist + recommendations
  useEffect(() => {
    const customerId = secureLocalStorage.getItem('customerId');
    if (customerId) {
      setIsLoggedIn(true);
      loadWishlist();
    } else {
      // Fall back to localStorage for non-authenticated users
      const savedWishlist = localStorage.getItem('wishlist');
      if (savedWishlist) {
        setWishlistItems(JSON.parse(savedWishlist));
      }
    }
    // Load recommended products
    loadRecommendedProducts();
  }, []);

  const loadWishlist = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await accountService.fetchWishlist(1, 100);
      if (result.success) {
        setWishlistItems(result.items || []);
      } else {
        setError(result.error || 'Failed to load wishlist');
      }
    } catch (err) {
      console.error('Error loading wishlist:', err);
      setError('An error occurred while loading your wishlist');
    } finally {
      setLoading(false);
    }
  };

  const loadRecommendedProducts = async () => {
    try {
      setLoadingRecommendations(true);
      const result = await recommendationsService.loadRecommendedProducts(1, 8);
      if (result.success) {
        setRecommendedProducts(result.data || []);
      }
    } catch (error) {
      console.error('Error loading recommended products:', error);
      // Silently fail - recommendations are nice to have but not critical
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const toggleWishlistItem = async (product) => {
    const productId = product._id || product.id;
    setToggledItemId(productId);

    try {
      if (isLoggedIn) {
        // Use API for authenticated users - toggle via accountService
        const result = await accountService.toggleWishlistItem(product);
        if (result.success) {
          if (result.inWishlist) {
            // Item was added - don't remove from display
            setSnackbarMessage('Added to wishlist ❤️');
          } else {
            // Item was removed - remove from display
            setWishlistItems(wishlistItems.filter(item => (item._id || item.id) !== productId));
            setSnackbarMessage('Removed from wishlist');
          }
          setSnackbarOpen(true);
        } else {
          setError(result.error || 'Failed to update wishlist');
        }
      } else {
        // Use localStorage for non-authenticated users
        const isCurrentlyInWishlist = wishlistItems.some(item => item.id === productId);
        if (isCurrentlyInWishlist) {
          // Remove from wishlist
          const updatedWishlist = wishlistItems.filter(item => item.id !== productId);
          setWishlistItems(updatedWishlist);
          localStorage.setItem('wishlist', JSON.stringify(updatedWishlist));
          setSnackbarMessage('Removed from wishlist');
        } else {
          // Add to wishlist
          const updatedWishlist = [...wishlistItems, product];
          setWishlistItems(updatedWishlist);
          localStorage.setItem('wishlist', JSON.stringify(updatedWishlist));
          setSnackbarMessage('Added to wishlist ❤️');
        }
        setSnackbarOpen(true);
      }
    } finally {
      setToggledItemId(null);
    }
  };

  const removeFromWishlist = async (productId) => {
    toggleWishlistItem(wishlistItems.find(item => (item._id || item.id) === productId));
  };

  const handleClearClick = () => {
    setClearConfirmOpen(true);
  };

  const handleClearConfirm = async () => {
    setClearConfirmOpen(false);
    setIsClearing(true);

    try {
      if (isLoggedIn) {
        // Use API for authenticated users
        const result = await accountService.clearWishlist();
        if (result.success) {
          setWishlistItems([]);
          setSnackbarMessage('Wishlist cleared successfully');
          setSnackbarOpen(true);
        } else {
          setError(result.error || 'Failed to clear wishlist');
        }
      } else {
        // Use localStorage for non-authenticated users
        setWishlistItems([]);
        localStorage.removeItem('wishlist');
        setSnackbarMessage('Wishlist cleared successfully');
        setSnackbarOpen(true);
      }
    } finally {
      setIsClearing(false);
    }
  };

  const handleClearCancel = () => {
    setClearConfirmOpen(false);
  };

  const addAllToCart = () => {
    // Add all wishlist items to cart using centralized API
    wishlistItems.forEach(item => {
      cartApi.addToCart(item);
    });
    
    // Show success message
    setSnackbarMessage(`🛒 All ${wishlistItems.length} items added to cart!`);
    setSnackbarOpen(true);
  };

  const moveToCart = (product) => {
    // Add item to cart using centralized API
    cartApi.addToCart(product);

    // Remove from wishlist
    removeFromWishlist(product.id);

    // Show success message
    setSnackbarMessage(`🛒 ${product.name} added to cart!`);
    setSnackbarOpen(true);
  };

  // Geometric background style
  const backgroundStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      linear-gradient(135deg, color-mix(in srgb, var(--primary) 3%, transparent) 0%, color-mix(in srgb, var(--primary) 1%, transparent) 100%),
      repeating-linear-gradient(45deg, transparent, transparent 35px, color-mix(in srgb, var(--primary) 2%, transparent) 35px, color-mix(in srgb, var(--primary) 2%, transparent) 70px),
      repeating-linear-gradient(-45deg, transparent, transparent 35px, color-mix(in srgb, var(--primary) 1%, transparent) 35px, color-mix(in srgb, var(--primary) 1%, transparent) 70px)
    `,
    zIndex: -1,
  };

  return (
    <>
      <Seo title="Wishlist" noindex />
      <Box sx={backgroundStyle} />
      <Container maxWidth="lg" sx={{ py: 6, position: 'relative' }}>
        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress sx={{ color: 'var(--primary)' }} />
          </Box>
        ) : (
          <>
            {/* Action Buttons */}
            {wishlistItems.length > 0 && (
              <Fade in timeout={600}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mb: 4 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleClearClick}
                    startIcon={<Delete />}
                    disabled={isClearing}
                    sx={{
                      borderRadius: '6px',
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2,
                        bgcolor: alpha('#f44336', 0.05),
                      },
                    }}
                  >
                    Clear All
                  </Button>
                  <Button
                    variant="contained"
                    onClick={addAllToCart}
                    startIcon={<ShoppingCart />}
                    size="large"
                    sx={{
                      borderRadius: '6px',
                      background: 'linear-gradient(135deg, var(--primary) 0%, var(--button-hover) 100%)',
                      boxShadow: '0 4px 15px color-mix(in srgb, var(--primary) 30%, transparent)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, var(--button-hover) 0%, #018580 100%)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 6px 20px color-mix(in srgb, var(--primary) 40%, transparent)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Add All to Cart
                  </Button>
                </Box>
              </Fade>
            )}

            {wishlistItems.length === 0 ? (
              // Empty State
              <Fade in timeout={800}>
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      background: 'color-mix(in srgb, var(--primary) 10%, transparent)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      mb: 3,
                    }}
                  >
                    <FavoriteBorder sx={{ fontSize: 60, color: 'var(--primary)' }} />
                  </Box>
                  <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    Your wishlist is empty
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: '1.1rem', mb: 4 }}>
                    Save your favorite items here to buy them later
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/products')}
                    startIcon={<ShoppingCart />}
                    sx={{
                      borderRadius: '6px',
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      background: 'linear-gradient(135deg, var(--primary) 0%, var(--button-hover) 100%)',
                      boxShadow: '0 4px 15px color-mix(in srgb, var(--primary) 30%, transparent)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, var(--button-hover) 0%, #018580 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px color-mix(in srgb, var(--primary) 40%, transparent)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Start Shopping
                  </Button>
                </Box>
              </Fade>
            ) : (
              <>
                {/* Info Alert */}
                <Fade in timeout={800}>
                  <Alert
                    severity="info"
                    sx={{
                      mb: 5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'info.light',
                      bgcolor: alpha('#0288d1', 0.05),
                    }}
                    icon={<LocalOffer />}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Items in your wishlist are not reserved
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Add them to your cart to secure your purchase before they sell out!
                      </Typography>
                    </Box>
                  </Alert>
                </Fade>

                <Grid container spacing={3}>
                  {wishlistItems.map((product, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                      <Fade in timeout={600 + index * 100}>
                        <Paper
                          sx={{
                            position: 'relative',
                            height: '100%',
                            borderRadius: 2,
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            border: '1px solid',
                            borderColor: 'divider',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                              borderColor: 'color-mix(in srgb, var(--primary) 30%, transparent)',
                            },
                          }}
                        >
                          {/* Wishlist badge */}
                          <Chip
                            label="In Wishlist"
                            size="small"
                            icon={<Favorite sx={{ fontSize: 16 }} />}
                            sx={{
                              position: 'absolute',
                              top: 12,
                              left: 12,
                              zIndex: 2,
                              bgcolor: alpha('#f44336', 0.9),
                              color: 'white',
                              fontWeight: 600,
                            }}
                          />

                          {/* Heart toggle button */}
                          <IconButton
                            onClick={() => toggleWishlistItem(product)}
                            disabled={toggledItemId === (product._id || product.id)}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              zIndex: 2,
                              bgcolor: alpha('#ffffff', 0.9),
                              backdropFilter: 'blur(4px)',
                              '&:hover': {
                                bgcolor: '#ffffff',
                                transform: 'scale(1.1)',
                              },
                              '&:disabled': {
                                opacity: 0.7,
                              },
                              transition: 'all 0.2s',
                            }}
                          >
                            {toggledItemId === (product._id || product.id) ? (
                              <CircularProgress
                                size={20}
                                sx={{
                                  color: '#ff4444',
                                }}
                              />
                            ) : (
                              <Favorite
                                sx={{
                                  fontSize: 20,
                                  color: '#ff4444',
                                  animation: 'pulse 0.3s ease',
                                  '@keyframes pulse': {
                                    '0%': { transform: 'scale(1)' },
                                    '50%': { transform: 'scale(1.2)' },
                                    '100%': { transform: 'scale(1)' },
                                  },
                                }}
                              />
                            )}
                          </IconButton>

                          <ProductCard product={product} isInWishlist={true} />

                          {/* Actions */}
                          <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                            <Button
                              fullWidth
                              variant="contained"
                              onClick={() => moveToCart(product)}
                              startIcon={<ShoppingCart />}
                              sx={{
                                borderRadius: '6px',
                                background: 'linear-gradient(135deg, var(--primary) 0%, var(--button-hover) 100%)',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, var(--button-hover) 0%, #018580 100%)',
                                  transform: 'translateY(-1px)',
                                },
                                transition: 'all 0.2s',
                              }}
                            >
                              Add to Cart
                            </Button>
                          </Box>
                        </Paper>
                      </Fade>
                    </Grid>
                  ))}
                </Grid>

                {/* Recommendations — hidden entirely when there is nothing to recommend */}
                {(loadingRecommendations || recommendedProducts.length > 0) && (
                <Box sx={{ mt: 10 }}>
                  <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TrendingUp sx={{ color: 'var(--primary)', fontSize: 28 }} />
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      You might also like
                    </Typography>
                    <Chip
                      label="Trending"
                      size="small"
                      sx={{
                        bgcolor: 'color-mix(in srgb, var(--primary) 10%, transparent)',
                        color: 'var(--primary)',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  {loadingRecommendations ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : recommendedProducts.length > 0 ? (
                    <Grid container spacing={3}>
                      {recommendedProducts.map((product, index) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                          <Fade in timeout={1000 + index * 100}>
                            <Paper
                              sx={{
                                position: 'relative',
                                borderRadius: 2,
                                overflow: 'hidden',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                                },
                              }}
                            >
                              <ProductCard product={product} />
                            </Paper>
                          </Fade>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        No recommended products available
                      </Typography>
                    </Box>
                  )}
                </Box>
                )}
              </>
            )}
          </>
        )}

        {/* Clear Wishlist Confirmation Dialog */}
        <Dialog
          open={clearConfirmOpen}
          onClose={handleClearCancel}
          PaperProps={{
            sx: {
              borderRadius: 2,
              minWidth: 380,
            }
          }}
        >
          <DialogTitle
            sx={{
              fontSize: '1.25rem',
              fontWeight: 700,
              bgcolor: alpha('#f44336', 0.08),
              borderBottom: '2px solid',
              borderColor: 'divider',
              pb: 2,
            }}
          >
            Clear Entire Wishlist?
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              Are you sure you want to clear your entire wishlist? This action cannot be undone. All your saved items will be permanently removed.
            </Typography>
            <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor: alpha('#f44336', 0.05),
                borderLeft: '4px solid #f44336',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 500 }}>
                ⚠️ You have {wishlistItems.length} item{wishlistItems.length !== 1 ? 's' : ''} in your wishlist.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              pt: 2,
              px: 3,
              pb: 2,
              gap: 1,
            }}
          >
            <Button
              onClick={handleClearCancel}
              variant="outlined"
              disabled={isClearing}
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleClearConfirm}
              variant="contained"
              color="error"
              disabled={isClearing}
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                minWidth: 120,
              }}
            >
              {isClearing ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: 'inherit' }} />
                  Clearing...
                </>
              ) : (
                'Clear All'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success Snackbar with View Cart Action */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setSnackbarOpen(false);
                navigate('/cart');
              }}
              sx={{
                color: '#ffffff',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              VIEW CART
            </Button>
          }
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{
            '& .MuiSnackbarContent-root': {
              backgroundColor: 'var(--primary)',
              fontSize: '0.9rem',
              fontWeight: 500,
              borderRadius: '8px',
              boxShadow: '0 4px 12px color-mix(in srgb, var(--primary) 30%, transparent)',
            }
          }}
        />
      </Container>
    </>
  );
};

export default Wishlist;